import { Injectable } from '@angular/core';
import { AddLayerObject, Map, SourceSpecification } from 'maplibre-gl';
import { TLayer } from '../../types/explorer/sjmap2/TLayer';
import { TMapLibreLayerId } from '../../types/explorer/sjmap2/TMapLibreLayerId';

@Injectable({
  providedIn: 'root'
})
export class MapLibreLayersService {

  constructor() { }

    /**
     * Aggiunge una sorgente alla mappa. Se la sorgente è già presente, non la aggiunge di nuovo.
     * @param _map Mappa a cui aggiungere la sorgente
     * @param sourceId Id della sorgente da aggiungere alla mappa
     * @param sourceSpec Specifiche della sorgente da aggiungere alla mappa
     * @warning Se la sorgente è già presente, viene stampato un avviso nella console e la sorgente non viene aggiunta.
     * @errors Se la mappa, l'id della sorgente o le specifiche della sorgente non esistono, viene stampato un errore nella console e la sorgente non viene aggiunta.
     */
    addSourceToMap(_map: Map, sourceId: string, sourceSpec: SourceSpecification) {

        let hasErrors: boolean = false; 
        if (!_map) {
            console.error(`Impossibile aggiungere la sorgente ${sourceId} alla mappa: mappa non definita. Map:`, _map);
            hasErrors = true;
        }

        if (!sourceId) {
            console.error(`Impossibile aggiungere la sorgente: sourceId non definito.`);
            hasErrors = true;
        }

        if (!sourceSpec) {
            console.error(`Impossibile aggiungere la sorgente ${sourceId} alla mappa: sourceSpec non definito. sourceSpec:`, sourceSpec);
            hasErrors = true;
        }

        if (_map.getSource(sourceId)) {
            // console.warn(`La sorgente ${sourceId} è già presente nella mappa. Non verrà aggiunta di nuovo.`);
            hasErrors = true;
        }

        if (hasErrors) return; // Così non termina al primo errore ma li stampa tutti insieme

        _map.addSource(sourceId, sourceSpec)
    }

    /**
     * Aggiunge un layer alla mappa. Se il layer è già presente, non lo aggiunge di nuovo.
     * Se il layer ha un upperLayerId, lo aggiunge sotto quel layer. Se non ha un upperLayerId, lo aggiunge sopra tutti gli altri layer.
     * @param _map Mappa a cui aggiungere il layer
     * @param layer Layer da aggiungere alla mappa
     * @param upperLayerId Id del layer sotto cui aggiungere il layer. Se non è definito, il layer verrà aggiunto sopra tutti gli altri layer.
     * @warning Se il layer è già presente, viene stampato un avviso nella console e il layer non viene aggiunto.
     * @errors Se la mappa o il layer da spostare non esistono, viene stampato un errore nella console e il layer non viene aggiunto.
     * @errors Se viene passato un upperLayerId ma il layer non esiste, viene stampato un errore nella console e il layer non viene aggiunto.
     * @errors Se viene trovato un layer di disegno ma non esiste su mappa, viene stampato un errore nella console e il layer non viene aggiunto.
     */
    addLayerToMap(_map: Map, layer: AddLayerObject, upperLayerId?: string) {
        
        let hasErrors: boolean = false;
        if (!_map) {
            console.error(`Impossibile aggiungere il layer ${layer.id} alla mappa: mappa non definita. Map:`, _map);
            hasErrors = true;
        }

        if (!layer) {
            console.error(`Impossibile aggiungere il layer alla mappa: layer non definito. Layer:`, layer);
            hasErrors = true;
        }

        if (upperLayerId && !_map.getLayer(upperLayerId)) { // Se upperLayerId è definito ma non esiste su mappa
            console.error(`Impossibile aggiungere il layer ${layer.id} alla mappa: layer precedente ${upperLayerId} non esistente. Layer precedente:`, _map.getLayer(upperLayerId));
            hasErrors = true;
        }

        if (_map.getLayer(layer.id)) {
            // console.warn(`Il layer ${layer.id} è già presente nella mappa. Non verrà aggiunto di nuovo.`);
            hasErrors = true;
        }

        if (hasErrors) return; // Così non termina al primo errore ma li stampa tutti insieme

        if (upperLayerId) {
            _map.addLayer(layer, upperLayerId); // Aggiungi il layer SOTTO il layer specificato da upperLayerId
            return;
        }

        const bottomDrawLayerId: string = this.getBottomDrawLayerId(_map); // Cerca il layer di disegno più in basso (nell'array di maplibre)
        if (bottomDrawLayerId) {
            if (!_map.getLayer(bottomDrawLayerId)) { // Se bottomDrawLayerId è definito ma non esiste su mappa
                console.error(`Impossibile aggiungere il layer ${layer.id} alla mappa: layer di disegno ${bottomDrawLayerId} non esistente. Layer di disegno:`, _map.getLayer(bottomDrawLayerId));
            } else {
                _map.addLayer(layer, bottomDrawLayerId); // Aggiungi il layer sotto il layer di disegno
            }
            return;
        }
        
        _map.addLayer(layer); // Aggiungi il layer IN CIMA alla mappa (sopra tutti gli altri layer)

    }

    /**
     * Sposta un layer sulla mappa
     * @param _map Mappa in cui avviene lo spostamento del layer
     * @param layerId Id del layer da spostare
     * @param upperLayerId Id del layer sotto cui spostare il layer. Se non è definito, il layer verrà spostato sopra tutti gli altri layer.
     * @errors Se la mappa o il layer non esistono, viene stampato un errore nella console e il layer non viene spostato.
     * @errors Se viene passato un upperLayerId ma il layer non esiste, viene stampato un errore nella console e il layer non viene spostato.
     * @errors Se viene trovato un layer di disegno ma non esiste su mappa, viene stampato un errore nella console e il layer non viene spostato.
     */
    moveLayerOnMap(_map: Map, layerId: string, upperLayerId?: string) {
        let hasErrors: boolean = false;
        if (!_map) {
            console.error(`Impossibile spostare il layer ${layerId} sulla mappa: mappa non definita. Map:`, _map);
            hasErrors = true;
        }

        if (!layerId) {
            console.error(`Impossibile spostare il layer: layerId non definito.`);
            hasErrors = true;
        }

        if(layerId && !_map.getLayer(layerId)) { // Se layerId è definito ma non esiste su mappa 
            console.error(`Impossibile spostare il layer ${layerId} sulla mappa: layer non esistente. Layer:`, _map.getLayer(layerId));
            hasErrors = true;
        }

        if (upperLayerId && !_map.getLayer(upperLayerId)) { // Se upperLayerId è definito ma non esiste su mappa
            console.error(`Impossibile spostare il layer ${layerId} sulla mappa: layer precedente ${upperLayerId} non esistente. Layer precedente:`, _map.getLayer(upperLayerId));
            hasErrors = true;
        }

        if (hasErrors) return; // Così non termina al primo errore ma li stampa tutti insieme

        if (upperLayerId) {
            _map.moveLayer(layerId, upperLayerId); // Sposta il layer sopra il layer specificato da upperLayerId
            return;
        }

        const bottomDrawLayerId: string = this.getBottomDrawLayerId(_map); // Cerca il layer di disegno più in basso (nell'array di maplibre)
        if (bottomDrawLayerId) {
            if (!_map.getLayer(bottomDrawLayerId)) { // Se bottomDrawLayerId è definito ma non esiste su mappa
                console.error(`Impossibile spostare il layer ${layerId} sulla mappa: layer di disegno ${bottomDrawLayerId} non esistente. Layer di disegno:`, _map.getLayer(bottomDrawLayerId));
            } else {
                _map.moveLayer(layerId, bottomDrawLayerId); // Sposta il layer sopra il layer di disegno
            }
            return;
        }

        _map.moveLayer(layerId); // Sposta il layer IN CIMA alla mappa (sopra tutti gli altri layer)

    }

    /**
     * Controlla se il layer ha i dati di maplibre. Se il layer è di tipo raster, controlla se ogni sorgente ha i dati di maplibre. Se il layer è di tipo vector, controlla se ogni sorgente ha i dati di maplibre e se ha almeno un layer.
     * @param layer Layer da controllare
     * @returns true se il layer ha i dati di maplibre, false altrimenti
     */
    hasMapLibreData(layer: TLayer): boolean {
        if(layer.type === 'raster') {
            return layer.sources.every(source => source.maplibre?.source && source.maplibre?.layer);
        } else if(layer.type === 'vector') {
            return layer.sources.every(source => source.maplibre?.source && source.maplibre?.layers?.length > 0);
        } else {
            return false; // Tipo di layer non supportato
        }
    }

    /**
     * Prende l'id del layer di disegno più in basso (sotto a tutti gli altri sulla mappa) di MapLibre.
     * @param mapInstance Mappa da cui prendere l'id del layer di disegno
     * @errors Se la mappa non esiste, viene stampato un errore nella console e viene restituito null.
     * @returns un id di un layer di MapLibre (stringa) o null se non esiste
     */
    getBottomDrawLayerId(mapInstance: Map): string {
        if (!mapInstance) {
            console.error(`Impossibile ottenere l'id del layer di disegno: mappa non definita. Map:`, mapInstance);
            return null;
        }
        return mapInstance.getStyle().layers.find(l => l.id.startsWith('gl-draw'))?.id || null; // Cerca il layer di disegno più in basso (nell'array di maplibre)
    }

    /**
     * Prende l'id del layer di MapLibre più in basso (sotto a tutti gli altri sulla mappa) del layer SJMap passato come parametro.
     * @param SJMapLayer Layer da cui prendere l'id del layer di MapLibre
     * @returns un id di un layer di MapLibre (stringa) o una stringa vuota se il layer non è supportato
     */
    getBottomMLLayerIdFromSJMLayer(SJMapLayer: TLayer): TMapLibreLayerId {
        if(SJMapLayer.type === 'raster') {
            // Prendi l'id dell'ultimo mapLibrelayer delle sources del layer. Questo id è l'id del layer che sta più sotto su maplibre (perché vengono inseriti sempre "in coda", quindi l'ultimo va a finire più sotto)
            const lastSourceIndex = SJMapLayer.sources.length - 1;
            const lastSource = SJMapLayer.sources[lastSourceIndex];
            const lastLayer = lastSource.maplibre.layer;
            return lastLayer;
        } else if(SJMapLayer.type === 'vector') {
            // Prendi l'id dell'ultimo mapLibrelayer del layer. Questo id è l'id del layer che sta più sotto su maplibre (perché vengono inseriti sempre "in coda", quindi l'ultimo va a finire più sotto)
            const lastSourceIndex = SJMapLayer.sources.length - 1;
            const lastSource = SJMapLayer.sources[lastSourceIndex];
            const lastLayerIndex = lastSource.maplibre.layers.length - 1;
            const lastLayer = lastSource.maplibre.layers[lastLayerIndex];
            return lastLayer;
        } else {
            return ""; // Tipo di layer non supportato
        }
    }
    removeLayersFromGroup(groupUUID: string, layers: TLayer[], map: Map): void {
        const layersToRemove = layers.filter(layer => layer.group_id === groupUUID);
      
        layersToRemove.forEach(layer => {
          if (!this.hasMapLibreData(layer)) return;
      
          if (layer.type === 'raster') {
            layer.sources.forEach(source => {
              const ml = source.maplibre;
              if (ml?.layer && map.getLayer(ml.layer)) {
                map.removeLayer(ml.layer);
              }
              if (ml?.source && map.getSource(ml.source)) {
                map.removeSource(ml.source);
              }
            });
          } else if (layer.type === 'vector') {
            layer.sources.forEach(source => {
              const ml = source.maplibre;
              ml?.layers?.forEach(layerId => {
                if (map.getLayer(layerId)) map.removeLayer(layerId);
                const labelLayerId = `${layerId}-label`;
                const labelSourceId = `${layerId}-label-source`;
                if (map.getLayer(labelLayerId)) map.removeLayer(labelLayerId);
                if (map.getSource(labelSourceId)) map.removeSource(labelSourceId);
              });
              if (ml?.source && map.getSource(ml.source)) {
                map.removeSource(ml.source);
              }
            });
          }
        });
      }
      

}
